import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "flex flex-col gap-4" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "flex flex-col" }, [
  /*#__PURE__*/_createElementVNode("h3", { class: "text-sm font-semibold text-gray-900" }, "Freshchat Support"),
  /*#__PURE__*/_createElementVNode("p", { class: "mt-1 text-sm text-gray-500" }, " Choose whether this school follows the school group's Freshchat setting or overrides it locally. ")
], -1)
const _hoisted_4 = { class: "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("label", {
  class: "text-sm font-medium text-gray-700",
  for: "freshchat-mode-select"
}, " Freshchat behaviour ", -1)
const _hoisted_6 = ["disabled"]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("option", { value: 'group' }, " Follow school group default ", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("option", { value: 'school' }, " Override for this school ", -1)
const _hoisted_9 = [
  _hoisted_7,
  _hoisted_8
]
const _hoisted_10 = { class: "flex items-center justify-between gap-3 sm:justify-start" }
const _hoisted_11 = { class: "flex flex-col" }
const _hoisted_12 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_13 = {
  key: 0,
  class: "mt-3 text-sm text-red-600"
}
const _hoisted_14 = {
  key: 1,
  class: "mt-3 text-sm text-green-600"
}

import { computed, ref, watch } from "vue";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import { useSchoolStore } from "@/stores/schoolStore";
import { useFilterStore } from "@/stores/filterStore";
import { useSchoolGroupStore } from "@/stores/schoolGroupsStore";
import { School } from "@/interfaces/school.interface";
import { showToast } from "@/utils/toastUtils";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/vue/20/solid";

type FreshchatMode = "group" | "school";

export default /*#__PURE__*/_defineComponent({
  __name: 'FreshchatSettingsCard',
  setup(__props) {

const schoolStore = useSchoolStore();
const filterStore = useFilterStore();
const schoolGroupStore = useSchoolGroupStore();

const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const currentSchool = computed<School | null>(() => {
  const school = schoolStore.getCurrentSchool;
  return school ? (school as School) : null;
});

const localValue = ref(false);
const localMode = ref<FreshchatMode>("group");

const overrideEnabled = computed(() => localMode.value === "school");

const deriveModeFromSchool = (school: School | null): FreshchatMode => {
  if (!school?.settings) {
    return "group";
  }

  if (school.settings.freshchatMode === "school") {
    return "school";
  }

  if (school.settings.freshchatMode === "group") {
    return "group";
  }

  if (school.settings.freshchat === true) {
    return "school";
  }

  return "group";
};

const extractFreshchatFlag = (source: unknown): boolean | undefined => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  const candidate = source as { settings?: { freshchat?: unknown } };
  const value = candidate.settings?.freshchat;

  return typeof value === "boolean" ? value : undefined;
};

const resolveGroupFreshchatSetting = (): boolean | undefined => {
  const school = currentSchool.value;

  if (!school) {
    return undefined;
  }

  let groupId: string | undefined;

  if (typeof school.schoolGroup === "string") {
    groupId = school.schoolGroup;
  } else if (
    school.schoolGroup &&
    typeof school.schoolGroup === "object" &&
    "_id" in (school.schoolGroup as Record<string, unknown>)
  ) {
    groupId = (school.schoolGroup as { _id?: string })._id;
  }

  if (!groupId) {
    groupId = filterStore.selectedFilters.schoolGroup || filterStore.selectedSchoolGroup?._id;
  }

  if (!groupId) {
    return undefined;
  }

  const candidates: unknown[] = [];

  const activeGroup = schoolGroupStore.getCurrentSchoolGroup;
  if (activeGroup && activeGroup._id === groupId) {
    candidates.push(activeGroup);
  }

  for (const group of schoolGroupStore.getSchoolGroups) {
    if (group._id === groupId) {
      candidates.push(group);
      break;
    }
  }

  const selectedGroup = filterStore.selectedSchoolGroup;
  if (selectedGroup && selectedGroup._id === groupId) {
    candidates.push(selectedGroup);
  }

  if (filterStore.filterOptions) {
    const fromFilters = filterStore.filterOptions.schoolGroups.find((group) => group._id === groupId);
    if (fromFilters) {
      candidates.push(fromFilters);
    }
  }

  for (const candidate of candidates) {
    const value = extractFreshchatFlag(candidate);
    if (typeof value === "boolean") {
      return value;
    }
  }

  return undefined;
};

const groupFreshchatDefault = computed(() => {
  const resolved = resolveGroupFreshchatSetting();
  return typeof resolved === "boolean" ? resolved : false;
});

watch(
  currentSchool,
  (school) => {
    localMode.value = deriveModeFromSchool(school);
    const freshchatSetting = school?.settings?.freshchat;
    if (localMode.value === "group") {
      localValue.value = groupFreshchatDefault.value;
    } else {
      const storedValue = typeof freshchatSetting === "boolean" ? freshchatSetting : false;
      localValue.value = storedValue;
    }
  },
  { immediate: true }
);

watch(
  groupFreshchatDefault,
  (value) => {
    if (!overrideEnabled.value) {
      localValue.value = value;
    }
  }
);

const updateFreshchatSetting = async (
  enabled: boolean,
  modeOverride?: FreshchatMode
) => {
  const schoolId =
    currentSchool.value?._id || filterStore.selectedFilters.school || "";

  if (!schoolId) {
    errorMessage.value = "Select a school to update Freshchat settings.";
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  successMessage.value = "";

  const result = await schoolStore.updateSchool(schoolId, {
    settings: {
      freshchat: enabled,
      freshchatMode: modeOverride ?? localMode.value,
    },
  });

  loading.value = false;

  if (result) {
    const nextMode = modeOverride ?? localMode.value;
    successMessage.value = nextMode === "school"
      ? enabled
        ? "Freshchat enabled for this school."
        : "Freshchat disabled for this school."
      : "Freshchat now follows the school group default.";
    showToast({
      text: successMessage.value,
      type: "success",
      icon: CheckCircleIcon,
    });
    const refreshed = schoolStore.getCurrentSchool;
    if (refreshed?.settings && refreshed._id) {
      filterStore.updateSchoolSettings(refreshed._id, refreshed.settings);
    }

    if (refreshed) {
      localMode.value = deriveModeFromSchool(refreshed);
      if (localMode.value === "group") {
        localValue.value = groupFreshchatDefault.value;
      } else {
        const updatedValue = typeof refreshed.settings?.freshchat === "boolean" ? refreshed.settings.freshchat : false;
        localValue.value = updatedValue;
      }
    }
  } else {
    errorMessage.value = "Unable to update Freshchat settings. Please try again.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon,
    });
    const current = currentSchool.value;
    localMode.value = deriveModeFromSchool(current);
    if (localMode.value === "group") {
      localValue.value = groupFreshchatDefault.value;
    } else {
      const revertValue = typeof current?.settings?.freshchat === "boolean" ? current.settings.freshchat : false;
      localValue.value = revertValue;
    }
  }
};

const onToggle = async (value: boolean) => {
  if (loading.value || !overrideEnabled.value) {
    return;
  }

  localValue.value = value;
  await updateFreshchatSetting(value, "school");
};

const onModeChange = async () => {
  if (loading.value) {
    return;
  }

  const nextMode = localMode.value;

  if (nextMode === "group") {
    localValue.value = groupFreshchatDefault.value;
    await updateFreshchatSetting(localValue.value, "group");
  } else {
    const school = currentSchool.value;
    const effectiveValue = typeof school?.settings?.freshchat === "boolean"
      ? school.settings.freshchat
      : localValue.value;
    localValue.value = effectiveValue;
    await updateFreshchatSetting(localValue.value, "school");
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _hoisted_5,
        _withDirectives(_createElementVNode("select", {
          id: "freshchat-mode-select",
          class: "w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-64",
          disabled: loading.value || !currentSchool.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localMode).value = $event)),
          onChange: onModeChange
        }, _hoisted_9, 40, _hoisted_6), [
          [_vModelSelect, localMode.value]
        ])
      ]),
      _createVNode(_unref(SwitchGroup), null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createVNode(_unref(SwitchLabel), { class: "text-sm font-medium text-gray-700" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(overrideEnabled.value ? (localValue.value ? 'Enabled' : 'Disabled') : 'Following school group default'), 1)
                ]),
                _: 1
              }),
              (!overrideEnabled.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_12, " Switch to “Override for this school” to control Freshchat here. "))
                : _createCommentVNode("", true)
            ]),
            _createVNode(_unref(Switch), {
              "model-value": localValue.value,
              disabled: loading.value || !overrideEnabled.value,
              "onUpdate:modelValue": onToggle,
              class: _normalizeClass([
              localValue.value ? 'bg-blue-600' : 'bg-gray-300',
              loading.value || !overrideEnabled.value ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2'
            ])
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", {
                  "aria-hidden": "true",
                  class: _normalizeClass([
                localValue.value ? 'translate-x-6' : 'translate-x-1',
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform'
              ])
                }, null, 2)
              ]),
              _: 1
            }, 8, ["model-value", "disabled", "class"])
          ])
        ]),
        _: 1
      })
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(errorMessage.value), 1))
      : (successMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(successMessage.value), 1))
        : _createCommentVNode("", true)
  ]))
}
}

})